import { useState } from "react";
import { toast } from "sonner";
import { submitConsultation } from "../lib/api";
import { CONSULTATION_TOPICS, TIME_PREFERENCES } from "../lib/constants";
import { CalendarCheck, Loader2 } from "lucide-react";

const today = () => new Date().toISOString().split("T")[0];

const initial = () => ({
  full_name: "",
  email: "",
  phone: "",
  preferred_date: today(),
  preferred_time: TIME_PREFERENCES[0].value,
  topic: CONSULTATION_TOPICS[0],
  notes: "",
});

export const ConsultationForm = ({ compact = false }) => {
  const [form, setForm] = useState(initial());
  const [loading, setLoading] = useState(false);

  const onChange = (e) =>
    setForm((s) => ({ ...s, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone) {
      toast.error("Please share your name, email, and phone so we can confirm.");
      return;
    }
    setLoading(true);
    try {
      await submitConsultation(form);
      toast.success("Consultation request received. We'll confirm your time shortly.");
      setForm(initial());
    } catch (err) {
      toast.error("Something went wrong. Please call us at 229-733-3500.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={onSubmit}
      data-testid="consultation-form"
      className={`bg-[color:var(--isa-soft-gray)] border border-gray-200 ${compact ? "p-6" : "p-8 lg:p-10"} rounded-sm`}
    >
      <div className="flex items-center gap-3 mb-6">
        <span className="eyebrow">Personalized Strategy Session</span>
      </div>
      <h3 className={`font-serif text-[color:var(--isa-navy)] ${compact ? "text-2xl" : "text-3xl"} leading-tight`}>
        Book a Consultation
      </h3>
      <p className="mt-2 text-sm font-sans text-gray-600">
        A complimentary, no-pressure conversation about your goals, your coverage, and
        your peace of mind. Schedule a preferred date and time below.
      </p>
      <div className="gold-divider" />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="md:col-span-2">
          <label className="isa-label" htmlFor="cons-name">Full Name *</label>
          <input
            id="cons-name"
            name="full_name"
            type="text"
            className="isa-input"
            value={form.full_name}
            onChange={onChange}
            required
            data-testid="consultation-input-name"
          />
        </div>
        <div>
          <label className="isa-label" htmlFor="cons-email">Email *</label>
          <input
            id="cons-email"
            name="email"
            type="email"
            className="isa-input"
            value={form.email}
            onChange={onChange}
            required
            data-testid="consultation-input-email"
          />
        </div>
        <div>
          <label className="isa-label" htmlFor="cons-phone">Phone *</label>
          <input
            id="cons-phone"
            name="phone"
            type="tel"
            className="isa-input"
            value={form.phone}
            onChange={onChange}
            required
            data-testid="consultation-input-phone"
          />
        </div>
        <div>
          <label className="isa-label" htmlFor="cons-date">Preferred Date *</label>
          <input
            id="cons-date"
            name="preferred_date"
            type="date"
            min={today()}
            className="isa-input"
            value={form.preferred_date}
            onChange={onChange}
            required
            data-testid="consultation-input-date"
          />
        </div>
        <div>
          <label className="isa-label" htmlFor="cons-time">Preferred Time *</label>
          <select
            id="cons-time"
            name="preferred_time"
            className="isa-input bg-white"
            value={form.preferred_time}
            onChange={onChange}
            required
            data-testid="consultation-input-time"
          >
            {TIME_PREFERENCES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="isa-label" htmlFor="cons-topic">What would you like to discuss? *</label>
          <select
            id="cons-topic"
            name="topic"
            className="isa-input bg-white"
            value={form.topic}
            onChange={onChange}
            required
            data-testid="consultation-input-topic"
          >
            {CONSULTATION_TOPICS.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="isa-label" htmlFor="cons-notes">Additional Notes</label>
          <textarea
            id="cons-notes"
            name="notes"
            rows={4}
            className="isa-input"
            value={form.notes}
            onChange={onChange}
            data-testid="consultation-input-notes"
            placeholder="Anything else we should know before our conversation..."
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={loading}
        className="btn-primary mt-7 w-full md:w-auto"
        data-testid="consultation-submit-button"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarCheck className="w-4 h-4" strokeWidth={1.8} />}
        {loading ? "Submitting..." : "Book My Consultation"}
      </button>
      <p className="mt-4 text-xs font-sans text-gray-500">
        We'll confirm your preferred time by phone or email within one business day.
      </p>
    </form>
  );
};

export default ConsultationForm;
